import React from 'react';
import { motion } from 'framer-motion';
import { Badge as BadgeType } from '../types';

interface BadgeProps {
  badge: BadgeType;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showDetails?: boolean;
  showExplanation?: boolean;
}

const Badge: React.FC<BadgeProps> = ({ 
  badge, 
  size = 'md', 
  className = '',
  showDetails = false,
  showExplanation = false
}) => {
  // Size classes
  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-base px-4 py-2'
  };

  // Badge type styles
  const badgeStyles = {
    perfect: {
      bg: 'bg-gradient-to-r from-brand-green-500 to-brand-blue-500',
      text: 'text-white',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    verified: {
      bg: 'bg-blue-500',
      text: 'text-white',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      )
    },
    organic: {
      bg: 'bg-green-500',
      text: 'text-white',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    sustainable: {
      bg: 'bg-teal-500',
      text: 'text-white',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  };

  const style = badgeStyles[badge.type];

  return (
    <div className={`${className}`}>
      <motion.div 
        className={`inline-flex items-center rounded-full ${style.bg} ${style.text} ${sizeClasses[size]} font-medium shadow-sm`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {style.icon}
        {badge.type === 'perfect' ? 'GoodCart Perfect 100%' : badge.type.charAt(0).toUpperCase() + badge.type.slice(1)}
      </motion.div>
      
      {showExplanation && badge.type === 'perfect' && (
        <div className="mt-2 text-xs text-gray-600">
          <p>This product meets our highest quality standards with a perfect 100% score across all evaluation criteria.</p>
        </div>
      )}
      
      {showDetails && (
        <div className="mt-2 text-xs text-gray-600">
          <p>Badge Code: {badge.code}</p>
          <p>Issued: {new Date(badge.issueDate).toLocaleDateString()}</p>
          {badge.validUntil && (
            <p>Valid Until: {new Date(badge.validUntil).toLocaleDateString()}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Badge;